/**
 * Serviço unificado que escolhe entre backend e Supabase baseado na configuração
 * Fornece uma interface única para todos os serviços da aplicação
 */

import { isBackendMode, isSupabaseMode } from '@/config/dataSource';
import { api as backendApi } from './api';
import { supabaseServices } from './supabase/adapters';
import { supabaseAuthService } from './supabase/authService';
import { comissaoSupabaseService } from './supabase/comissaoService';
import { financeiroSupabaseService } from './supabase/financeiroService';
import { configuracoesSupabaseService } from './supabase/configuracoesService';
import { SupabaseDashboardService } from './supabase/dashboardService';
import { gruposSupabaseService } from './supabase/gruposService';
import { cotasSupabaseService } from './supabase/cotasService';
import { bensSupabaseService } from './supabase/bensService';
import { unifiedPropostaComercialService } from './unifiedPropostaComercialService';

// Re-exporta os tipos necessários
export type { LoginRequest, LoginResponse, RegisterRequest, RegisterResponse, UserProfile } from './supabase/authService';

/**
 * Serviço unificado de autenticação
 */
export class UnifiedAuthService {
  async login(credentials: any) {
    const loginValue = credentials.email || credentials.username;
    const password = credentials.password;

    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/login', {
        email: loginValue,
        password
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.login({
        username: loginValue,
        password
      });
    }
    throw new Error('Fonte de dados não configurada');
  }

  async register(userData: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/register', userData);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.register(userData);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async refreshToken(refreshToken: string) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/refresh-token', {
        refresh_token: refreshToken,
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.refreshToken({ refresh_token: refreshToken });
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getCurrentUser() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/auth/me');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.getCurrentUser();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async logout() {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/logout');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.logout();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async requestPasswordReset(emailOrUsername: string) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/request-password-reset', { email: emailOrUsername });
      return response.data;
    } else if (isSupabaseMode()) {
      // No Supabase, aceita username ou email
      return await supabaseAuthService.requestPasswordReset(emailOrUsername);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async resetPassword(newPassword: string) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/auth/reset-password', { password: newPassword });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.resetPassword(newPassword);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async isAuthenticated() {
    if (isBackendMode()) {
      try {
        await backendApi.get('/api/auth/me');
        return true;
      } catch {
        return false;
      }
    } else if (isSupabaseMode()) {
      return await supabaseAuthService.isAuthenticated();
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de clientes
 */
export class UnifiedClienteService {
  async findAll() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/clientes');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.listarClientes(1, 1000);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/clientes/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.buscarCliente(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/clientes', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.criarCliente(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/clientes/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.atualizarCliente(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/clientes/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.excluirCliente(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByCpf(cpf: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/clientes/verificar-cpf?cpf=${cpf}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.verificarCPFExistente(cpf);
    }
    throw new Error('Fonte de dados não configurada');
  }

  // Métodos adicionais para compatibilidade com o serviço original
  async listarClientes(
    page = 1,
    per_page = 10,
    search?: string,
    tenantId?: string | number | number[],
    filterByUserId?: string
  ) {
    if (isBackendMode()) {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: per_page.toString(),
        ...(search && { search }),
        ...(tenantId != null && !Array.isArray(tenantId) && { tenant_id: String(tenantId) }),
      });
      const response = await backendApi.get('/api/clientes', { params });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.listarClientes(page, per_page, search, tenantId, filterByUserId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async excluirClientesEmLote(ids: string[]) {
    if (isBackendMode()) {
      const response = await backendApi.delete('/api/clientes/delete-multiple', {
        data: { ids: ids.map(id => parseInt(id)) }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.excluirClientesEmLote(ids);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async uploadDocumento(clienteId: string, file: File, tipo: string, tenantId?: string, userId?: string) {
    if (isBackendMode()) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);
      const response = await backendApi.post(`/api/clientes/${clienteId}/documentos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.uploadDocumento(clienteId, file, tipo, tenantId, userId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async listarDocumentos(clienteId: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/clientes/${clienteId}/documentos`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.listarDocumentos(clienteId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async excluirDocumento(clienteId: string, documentoId: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/clientes/${clienteId}/documentos/${documentoId}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.excluirDocumento(clienteId, documentoId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getDocumentUrl(path: string) {
    if (isBackendMode()) {
      // Para backend, usar endpoint público
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      return `${API_BASE_URL}/api/storage/public/${path}`;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.getDocumentUrl(path);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async verificarCPFExistente(cpf: string, ignoreClienteId?: string) {
    if (isBackendMode()) {
      const params = new URLSearchParams({
        cpf: cpf.replace(/\D/g, ''),
        ...(ignoreClienteId && { ignore_cliente_id: ignoreClienteId })
      });
      const response = await backendApi.get('/api/clientes/verificar-cpf', { params });
      return response.data.exists;
    } else if (isSupabaseMode()) {
      return await supabaseServices.clientes.verificarCPFExistente(cpf, ignoreClienteId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de grupos
 */
export class UnifiedGrupoService {
  async findAll() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/grupos');
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.findAll();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/grupos/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.findById(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/grupos', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.create(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/grupos/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.update(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/grupos/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.delete(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByNumero(numero: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/grupos?numero=${numero}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.findByNumero(numero);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByEspecie(especie: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/grupos?especie=${especie}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.findByEspecie(especie);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByTenantId(tenantId: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/grupos?tenant_id=${tenantId}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await gruposSupabaseService.findByTenantId(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de cotas
 */
export class UnifiedCotasService {
  async findAll() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/cotas');
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.findAll();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByGrupo(grupoId: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/cotas/grupo/${grupoId}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.findByGrupo(grupoId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByGrupoWithRelations(grupoId: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/cotas/grupo/${grupoId}/relations`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.findByGrupoWithRelations(grupoId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/cotas/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.findById(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByIdWithRelations(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/cotas/${id}/relations`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.findByIdWithRelations(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/cotas', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.create(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/cotas/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.update(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/cotas/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.delete(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async ocuparCota(id: string, clienteId: string, contratoId: string) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/cotas/${id}/ocupar`, {
        cliente_id: clienteId,
        contrato_id: contratoId
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.ocuparCota(id, clienteId, contratoId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async liberarCota(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/cotas/${id}/liberar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.liberarCota(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async contemplarCota(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/cotas/${id}/contemplar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.contemplarCota(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async quitarCota(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/cotas/${id}/quitar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await cotasSupabaseService.quitarCota(id);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de contratos
 */
export class UnifiedContratoService {
  async findAll(filterByUserId?: string, tenantId?: string | number | number[]) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contratos');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.findAll(filterByUserId, tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/contratos/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.findById(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/contratos', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.create(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/contratos/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.update(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/contratos/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.delete(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByClienteId(clienteId: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/contratos?cliente_id=${clienteId}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratos.findByClienteId(clienteId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de bens
 */
export class UnifiedBemService {
  async findAll() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/bens');
      return response.data;
    } else if (isSupabaseMode()) {
      return await bensSupabaseService.findAll();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/bens/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await bensSupabaseService.findById(parseInt(id));
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/bens', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await bensSupabaseService.create(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/bens/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await bensSupabaseService.update(parseInt(id), data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/bens/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      await bensSupabaseService.delete(parseInt(id));
      return { success: true };
    }
    throw new Error('Fonte de dados não configurada');
  }
  async findByGrupoId(grupoId: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/grupos/${grupoId}/bens`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await bensSupabaseService.findByGrupoId(grupoId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de consultores
 */
export class UnifiedConsultorService {
  async findAll(tenantId?: string | number) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/consultores');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.listarConsultores(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findById(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/consultores/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.buscarConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/consultores', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.criarConsultor(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/consultores/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.atualizarConsultor(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async delete(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.delete(`/api/consultores/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.excluirConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  // Métodos adicionais para compatibilidade com o serviço original
  async listarConsultores(tenantId?: string | number) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/consultores');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.listarConsultores(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async buscarConsultor(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/consultores/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.buscarConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async criarConsultor(consultor: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/consultores', consultor);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.criarConsultor(consultor);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async atualizarConsultor(id: string, consultor: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/consultores/${id}`, consultor);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.atualizarConsultor(id, consultor);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async excluirConsultor(id: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/consultores/${id}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.excluirConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async listarEquipe(supervisorId: string, tenantId?: string | number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/consultores/${supervisorId}/equipe`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.listarEquipe(supervisorId, tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async listarRelacionadores(liderId: string, tenantId?: string | number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/consultores/${liderId}/relacionadores`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.listarRelacionadores(liderId, tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async listarSupervisores(tenantId?: string | number) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/consultores/supervisores');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.listarSupervisores(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async ativarConsultor(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/consultores/${id}/ativar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.ativarConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async inativarConsultor(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/consultores/${id}/inativar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.consultores.inativarConsultor(id);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de comissões
 */
export class UnifiedComissaoService {
  async listarComissoes(filtros?: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/comissoes', { params: filtros });
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.listarComissoes(filtros);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async buscarComissao(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/comissoes/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.buscarComissao(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async criarComissao(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/comissoes', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.criarComissao(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async atualizarStatusPagamento(id: string, status: any, data_pagamento?: string) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/comissoes/${id}/status-pagamento`, { status, data_pagamento });
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.atualizarStatusPagamento(id, status, data_pagamento);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async gerarComissoesContrato(contrato_id: string) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/comissoes/gerar/${contrato_id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.gerarComissoesContrato(contrato_id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async exportarComissoes(options: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/comissoes/exportar', options);
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.exportarComissoes(options);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async listarComissoesHierarquica(filtros?: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/comissoes/hierarquica', { params: filtros });
      return response.data;
    } else if (isSupabaseMode()) {
      return await comissaoSupabaseService.listarComissoesHierarquica(filtros);
    }
    throw new Error('Fonte de dados não configurada');
  }
}


/**
 * Serviço unificado financeiro
 */
export class UnifiedFinanceiroService {
  async getResumoFinanceiro(tenantId: string, periodo: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/financeiro/resumo', { params: { tenantId, ...periodo } });
      return response.data;
    } else if (isSupabaseMode()) {
      return await financeiroSupabaseService.getResumoFinanceiro(tenantId, periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getFluxoCaixa(tenantId: string, meses?: number) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/financeiro/fluxo-caixa', { params: { tenantId, meses } });
      return response.data;
    } else if (isSupabaseMode()) {
      return await financeiroSupabaseService.getFluxoCaixa(tenantId, meses);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getPagamentosPendentes(tenantId: string) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/financeiro/pagamentos-pendentes', { params: { tenantId } });
      return response.data;
    } else if (isSupabaseMode()) {
      return await financeiroSupabaseService.getPagamentosPendentes(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getReceitas(tenantId: string, periodo: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/financeiro/receitas', { params: { tenantId, ...periodo } });
      return response.data;
    } else if (isSupabaseMode()) {
      return await financeiroSupabaseService.getReceitas(tenantId, periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getDespesas(tenantId: string, periodo: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/financeiro/despesas', { params: { tenantId, ...periodo } });
      return response.data;
    } else if (isSupabaseMode()) {
      return await financeiroSupabaseService.getDespesas(tenantId, periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

/**
 * Serviço unificado de dashboard
 */
export class UnifiedDashboardService {
  async getStats(tenantId?: string, startDateIso?: string, endDateIso?: string, periodo: string = 'thisMonth') {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/dashboard/stats', {
        params: { tenantId, startDate: startDateIso, endDate: endDateIso, periodo }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService(tenantId);
      const startDate = startDateIso ? new Date(startDateIso) : undefined;
      const endDate = endDateIso ? new Date(endDateIso) : undefined;
      return await dashboardService.getMetricasResumo(startDate, endDate, periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getTenantsPerformance(startDateIso?: string, endDateIso?: string, periodo: string = 'thisMonth') {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/superadmin/dashboard/stats', {
        params: { startDate: startDateIso, endDate: endDateIso, periodo }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService();
      const startDate = startDateIso ? new Date(startDateIso) : undefined;
      const endDate = endDateIso ? new Date(endDateIso) : undefined;
      return await dashboardService.getTenantsPerformance(startDate, endDate, periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getContracts(tenantId?: string, limit?: number) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/dashboard/contracts', { params: { tenantId, limit } });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService(tenantId);
      return await dashboardService.getUltimosContratos(limit || 20);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getUsers(tenantId?: string) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/dashboard/users', { params: { tenantId } });
      return response.data;
    } else if (isSupabaseMode()) {
      // Para Supabase, retorna dados básicos de usuários
      // TODO: Implementar método específico para usuários se necessário
      return {
        total: 0,
        ativos: 0,
        inativos: 0,
        novos: 0
      };
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getVendedorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string) {
    if (isBackendMode()) {
      const params: any = { periodo, userId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await backendApi.get('/api/dashboard/vendedor', { params });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService();
      return await dashboardService.getVendedorStats(periodo, userId, startDate, endDate);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getSupervisorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string) {
    if (isBackendMode()) {
      const params: any = { periodo, userId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await backendApi.get('/api/dashboard/supervisor', { params });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService();
      return await dashboardService.getSupervisorStats(periodo, userId, startDate, endDate);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getRelacionadorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string) {
    if (isBackendMode()) {
      const params: any = { periodo, userId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await backendApi.get('/api/dashboard/relacionador', { params });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService();
      return await dashboardService.getRelacionadorStats(periodo, userId, startDate, endDate);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getVendedorHistorico(periodo: string = '12months') {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/dashboard/vendedor/historico', { params: { periodo } });
      return response.data;
    } else if (isSupabaseMode()) {
      const dashboardService = new SupabaseDashboardService();
      return await dashboardService.getVendedorHistorico(periodo);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

// Instâncias singleton dos serviços unificados
export const unifiedAuthService = new UnifiedAuthService();
export const unifiedClienteService = new UnifiedClienteService();
export const unifiedGrupoService = new UnifiedGrupoService();
export const unifiedCotasService = new UnifiedCotasService();
export const unifiedBemService = new UnifiedBemService();
export const unifiedContratoService = new UnifiedContratoService();
export const unifiedConsultorService = new UnifiedConsultorService();
export const unifiedComissaoService = new UnifiedComissaoService();
export const unifiedFinanceiroService = new UnifiedFinanceiroService();
export const unifiedDashboardService = new UnifiedDashboardService();

/**
 * Serviço unificado de configurações
 */
export class UnifiedConfiguracoesService {
  async getCurrentUser() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/user/profile');
      return response.data;
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.getCurrentUser();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updateCurrentUser(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put('/api/user/profile', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.updateCurrentUser(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updateAvatar(file: File) {
    if (isBackendMode()) {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await backendApi.post('/api/user/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.updateAvatar(file);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updatePassword(data: any) {
    if (isBackendMode()) {
      await backendApi.put('/api/user/password', data);
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.updatePassword(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async terminateSession(sessionId: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/user/sessions/${sessionId}`);
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.terminateSession(sessionId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async toggleTwoFactor(enabled: boolean) {
    if (isBackendMode()) {
      await backendApi.post('/api/user/two-factor', { enabled });
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.toggleTwoFactor(enabled);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updateTenantSettings(tenantId: number, settings: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/tenants/${tenantId}/settings`, settings);
      return response.data;
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.updateTenantSettings(tenantId, settings);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getTenantSettings(tenantId: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/tenants/${tenantId}/settings`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await configuracoesSupabaseService.getTenantSettings(tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

export const unifiedConfiguracoesService = new UnifiedConfiguracoesService();

/**
 * Serviço unificado de propostas
 */
export class UnifiedPropostasService {
  async listarPropostas(filtros?: any, tenantId?: string) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/propostas', { params: filtros });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.listarPropostas(filtros, tenantId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async buscarProposta(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/propostas/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.buscarProposta(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async criarProposta(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/propostas', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.criarProposta(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async atualizarProposta(id: string, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/propostas/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.atualizarProposta(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async excluirProposta(id: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/propostas/${id}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.excluirProposta(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async aprovarProposta(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/propostas/${id}/aprovar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.aprovarProposta(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async rejeitarProposta(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/propostas/${id}/rejeitar`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.rejeitarProposta(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async atualizarStatus(id: string, status: any) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/propostas/${id}/status`, { status });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.atualizarStatus(id, status);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async uploadAnexos(id: string, files: FileList) {
    if (isBackendMode()) {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      const response = await backendApi.post(`/api/propostas/${id}/anexos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.uploadAnexos(id, files);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async removerAnexo(propostaId: string, anexoId: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/propostas/${propostaId}/anexos/${anexoId}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.propostas.removerAnexo(propostaId, anexoId);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

export const unifiedPropostasService = new UnifiedPropostasService();
export const unifiedPropostasComerciaisService = unifiedPropostaComercialService;

/**
 * Serviço unificado de SuperAdmin
 */
export class UnifiedSuperadminService {
  async getUsers() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/superadmin/users');
      return response.data;
    } else if (isSupabaseMode()) {
      const supabaseUsers = await supabaseServices.superadmin.getUsers();

      return supabaseUsers.map((user: any) => ({
        ...user,
        tenant_id: user.tenant_id ? user.tenant_id.toString() : null, // Converter para string para consistência
        tenant: user.tenant ? {
          ...user.tenant,
          id: user.tenant.id?.toString() // Garantir que o ID do tenant seja string
        } : undefined
      }));
    }
    throw new Error('Fonte de dados não configurada');
  }

  async createUser(userData: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/superadmin/users', userData);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.superadmin.createUser(userData);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updateUser(userId: string, userData: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/superadmin/users/${userId}`, userData);
      return response.data;
    } else if (isSupabaseMode()) {
      const supabaseUser = await supabaseServices.superadmin.updateUser(userId, userData);

      return {
        ...supabaseUser,
        tenant_id: supabaseUser.tenant_id ? supabaseUser.tenant_id.toString() : null,
        tenant: supabaseUser.tenant ? {
          ...supabaseUser.tenant,
          id: supabaseUser.tenant.id?.toString()
        } : undefined
      };
    }
    throw new Error('Fonte de dados não configurada');
  }

  async resetPassword(userId: string, newPassword: string, permanent: boolean = false) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/superadmin/users/${userId}/reset-password`, {
        new_password: newPassword,
        permanent: permanent
      });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.superadmin.resetPassword(userId, newPassword, permanent);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async checkUserDependencies(userId: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/superadmin/users/${userId}/dependencies`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.superadmin.checkUserDependencies(userId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async deleteUser(userId: string, forceUnlink: boolean = false) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/superadmin/users/${userId}`);
    } else if (isSupabaseMode()) {
      await supabaseServices.superadmin.deleteUser(userId, forceUnlink);
    } else {
      throw new Error('Fonte de dados não configurada');
    }
  }

  async getDashboardStats() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/superadmin/stats');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.superadmin.getDashboardStats();
    }
    throw new Error('Fonte de dados não configurada');
  }
}

export const unifiedSuperadminService = new UnifiedSuperadminService();

/**
 * Serviço unificado de Tenants
 */
export class UnifiedTenantService {
  async findAll() {
    return this.fetchTenants();
  }

  async fetchTenants() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/superadmin/tenants');
      return response.data;
    } else if (isSupabaseMode()) {
      const supabaseTenants = await supabaseServices.tenants.fetchTenants();

      // Converter para o formato esperado pelo frontend
      const convertedTenants = supabaseTenants.map((tenant: any) => ({
        ...tenant,
        id: tenant.id.toString(),
        ativo: tenant.status === 'ATIVO',
        _count: {
          users: tenant._count?.users || 0,
          contratos: tenant._count?.contratos || 0
        }
      }));

      return convertedTenants;
    }
    throw new Error('Fonte de dados não configurada');
  }

  async fetchTenant(id: string) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/superadmin/tenants/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      const supabaseTenant = await supabaseServices.tenants.fetchTenant(id);
      // Converter para o formato esperado pelo frontend
      return {
        ...supabaseTenant,
        id: supabaseTenant.id.toString(),
        ativo: supabaseTenant.status === 'ATIVO',
        _count: {
          users: supabaseTenant._count?.users || 0,
          contratos: supabaseTenant._count?.contratos || 0
        }
      };
    }
    throw new Error('Fonte de dados não configurada');
  }

  async createTenant(tenantData: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/superadmin/tenants', tenantData);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.tenants.createTenant(tenantData);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async updateTenant(tenantData: any) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/superadmin/tenants/${tenantData.id}`, tenantData);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.tenants.updateTenant(tenantData);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async deleteTenant(id: string) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/superadmin/tenants/${id}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.tenants.deleteTenant(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async toggleTenantStatus(id: string, active: boolean) {
    if (isBackendMode()) {
      const response = await backendApi.put(`/api/superadmin/tenants/${id}`, { status: active ? 'ATIVO' : 'INATIVO' });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.tenants.toggleTenantStatus(id, active);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

export const unifiedTenantService = new UnifiedTenantService();

/**
 * Serviço unificado de Controle de Qualidade de Contratos
 */
export class UnifiedContratoControleQualidadeService {
  async create(data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post('/api/contrato-checagens', data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.create(data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findAll() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contrato-checagens');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.findAll();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findOne(id: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/contrato-checagens/${id}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.findOne(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async findByContrato(contratoId: number) {
    if (isBackendMode()) {
      const response = await backendApi.get(`/api/contrato-checagens/contrato/${contratoId}`);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.findByContrato(contratoId);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async update(id: number, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.patch(`/api/contrato-checagens/${id}`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.update(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async iniciarControleQualidade(id: number, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/contrato-checagens/${id}/iniciar`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.iniciarControleQualidade(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async finalizarControleQualidade(id: number, data: any) {
    if (isBackendMode()) {
      const response = await backendApi.post(`/api/contrato-checagens/${id}/finalizar`, data);
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.finalizarControleQualidade(id, data);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async remove(id: number) {
    if (isBackendMode()) {
      await backendApi.delete(`/api/contrato-checagens/${id}`);
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.remove(id);
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getChecagensPendentes() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contrato-checagens/pendentes');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.getChecagensPendentes();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getChecagensEmAnalise() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contrato-checagens/em-analise');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.getChecagensEmAnalise();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getChecagensFinalizadas() {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contrato-checagens/finalizadas');
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.getChecagensFinalizadas();
    }
    throw new Error('Fonte de dados não configurada');
  }

  async getChecagensByFilters(filters: any) {
    if (isBackendMode()) {
      const response = await backendApi.get('/api/contrato-checagens', { params: filters });
      return response.data;
    } else if (isSupabaseMode()) {
      return await supabaseServices.contratoControleQualidade.getChecagensByFilters(filters);
    }
    throw new Error('Fonte de dados não configurada');
  }
}

export const unifiedContratoControleQualidadeService = new UnifiedContratoControleQualidadeService();

// Exporta todos os serviços em um objeto para facilitar o uso
export const unifiedServices = {
  auth: unifiedAuthService,
  clientes: unifiedClienteService,
  grupos: unifiedGrupoService,
  cotas: unifiedCotasService,
  bens: unifiedBemService,
  contratos: unifiedContratoService,
  consultores: unifiedConsultorService,
  comissoes: unifiedComissaoService,
  financeiro: unifiedFinanceiroService,
  dashboard: unifiedDashboardService,
  configuracoes: unifiedConfiguracoesService,
  propostas: unifiedPropostasService,
  propostasComerciais: unifiedPropostasComerciaisService,
  superadmin: unifiedSuperadminService,
  tenants: unifiedTenantService,
  contratoControleQualidade: unifiedContratoControleQualidadeService,
};
